import { IPoint } from '@src/slide/infrastructure/module/independent/graphic/point/point.interface';
import { IRect } from './rect/rect';

export function afterFlipH(position: IPoint, centerX: number) {
    return {
        x: centerX - (position.x - centerX),
        y: position.y,
    };
}

export function afterFlipV(position: IPoint, centerY: number) {
    return {
        x: position.x,
        y: centerY - (position.y - centerY),
    };
}

export function afterRotate(position: IPoint, rotate: number, centerX = 0, centerY = 0): IPoint { // 顺时针
    return {
        x: ((position.x - centerX) * Math.cos(angleToRadian(rotate)))
            - ((position.y - centerY) * Math.sin(angleToRadian(rotate)))
            + centerX,
        y: ((position.y - centerY) * Math.cos(angleToRadian(rotate)))
            + ((position.x - centerX) * Math.sin(angleToRadian(rotate)))
            + centerY,
    };
}

/**
 * 获取rect经过某个中心点翻转后的左上角坐标
 * @param rect 待翻转的矩形
 * @param center 翻转相对的中心点
 * @param flipH
 * @param flipV
 * @returns
 */
export function getLeftTopPointAfterFlip(rect: IRect, center: IPoint, flipH?: number, flipV?: number): IPoint {
    const cornerPoints = cornerPointsOfRect(rect) ?? [];
    let minX = 0;
    let minY = 0;
    cornerPoints.forEach((point, i) => {
        if (flipH === 1) {
            point = afterFlipH(point, center.x);
        }
        if (flipV === 1) {
            point = afterFlipV(point, center.y);
        }
        if (i === 0) {
            minX = point.x;
            minY = point.y;
        } else {
            minX = Math.min(minX, point.x);
            minY = Math.min(minY, point.y);
        }
    });
    return { x: minX, y: minY };
}

export const DIRECTION = {
    TOP: 0,
    RIGHT: 1,
    BOTTOM: 2,
    LEFT: 3,
};


export function isOppositeDirection(d1, d2) {
    if ((d1 === DIRECTION.LEFT && d2 === DIRECTION.RIGHT)
        || (d1 === DIRECTION.RIGHT && d2 === DIRECTION.LEFT)
        || (d1 === DIRECTION.TOP && d2 === DIRECTION.BOTTOM)
        || (d1 === DIRECTION.BOTTOM && d2 === DIRECTION.TOP)) {
        return true;
    }
    return false;
}

export function isHorizontalDirection(direction) {
    switch (direction) {
        case DIRECTION.LEFT:
        case DIRECTION.RIGHT:
            return true;
        case DIRECTION.TOP:
        case DIRECTION.BOTTOM:
            return false;
    }
}

export function isVerticalDirection(direction) {
    switch (direction) {
        case DIRECTION.LEFT:
        case DIRECTION.RIGHT:
            return false;
        case DIRECTION.TOP:
        case DIRECTION.BOTTOM:
            return true;
    }
}

export function oppositeDirection(direction) {
    switch (direction) {
        case DIRECTION.LEFT:
            return DIRECTION.RIGHT;
        case DIRECTION.RIGHT:
            return DIRECTION.LEFT;
        case DIRECTION.TOP:
            return DIRECTION.BOTTOM;
        case DIRECTION.BOTTOM:
            return DIRECTION.TOP;
    }
}

export function horizontalSymmetryRect(rect, x) { // 以x轴为中心求对称的矩形
    const newRect = {
        left: Math.min((2 * x) - rect.left, (2 * x) - rect.right),
        top: rect.top,
        right: Math.max((2 * x) - rect.left, (2 * x) - rect.right),
        bottom: rect.bottom,
    };
    return newRect;
}

export function verticalSymmetryRect(rect, y) {
    const newRect = {
        left: rect.left,
        top: Math.min((2 * y) - rect.top, (2 * y) - rect.bottom),
        right: rect.right,
        bottom: Math.max((2 * y) - rect.top, (2 * y) - rect.bottom),
    };
    return newRect;
}

export function rotateRectWithPoint(rect, p, rotate) {
    const p0 = pointAfterRotate({ x: rect.left, y: rect.top }, p, rotate);
    const p1 = pointAfterRotate({ x: rect.left, y: rect.top + rect.height }, p, rotate);
    const p2 = pointAfterRotate({ x: rect.left + rect.width, y: rect.top }, p, rotate);
    const p3 = pointAfterRotate({ x: rect.left + rect.width, y: rect.top + rect.height }, p, rotate);
    const minX = Math.min(p0.x, p1.x, p2.x, p3.x);
    const minY = Math.min(p0.y, p1.y, p2.y, p3.y);
    const maxX = Math.max(p0.x, p1.x, p2.x, p3.x);
    const maxY = Math.max(p0.y, p1.y, p2.y, p3.y);
    return {
        left: minX,
        top: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

export function pointAfterRotate(p, p1, rotate) {
    return {
        x: ((p.x - p1.x) * Math.cos(angleToRadian(rotate))) - ((p.y - p1.y) * Math.sin(angleToRadian(rotate))) + p1.x,
        y: ((p.x - p1.x) * Math.sin(angleToRadian(rotate))) + ((p.y - p1.y) * Math.cos(angleToRadian(rotate))) + p1.y,
    };
}

export function directionAfterRotate(direction, rotate) {
    if (rotate > 45 && rotate <= 135) {
        return (direction + 1) % 4;
    } if (rotate > 135 && rotate <= 225) {
        return (direction + 2) % 4;
    } if (rotate > 225 && rotate <= 315) {
        return (direction + 3) % 4;
    }
    return direction;
}

export function angleToRadian(angle) {
    return Math.PI / 180.0 * angle;
}

/**
 * 向量叉乘
 * @param p1
 * @param p2
 */
export function crossMultiply(v1, v2) {
    return (v1.x * v2.y) - (v2.x * v1.y);
}

/**
 * 向量点乘
 * @param p1
 * @param p2
 */
export function pointMultiply(v1, v2) {
    return (v1.x * v2.x) + (v1.y * v2.y);
}

/**
 * 点相对与向量的方向：1表示右手方向，-1表示左手方向，0表示在直线上
 */
export function pointToSegment(point, segment) {
    const v1 = {
        x: segment.p1.x - point.x,
        y: segment.p1.y - point.y,
    };
    const v2 = {
        x: segment.p2.x - point.x,
        y: segment.p2.y - point.y,
    };
    return crossMultiply(v1, v2);
}

/**
 * 两点之间的距离
 */
export function lengthBetweenTwoPoints(p1, p2) {
    return Math.sqrt(((p1.x - p2.x) * (p1.x - p2.x)) + ((p1.y - p2.y) * (p1.y - p2.y)));
}


export function polarToCartesian(centerX, centerY, rX, rY, angleInDegrees) {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;

    return {
        x: centerX + (rX * Math.cos(angleInRadians)),
        y: centerY + (rY * Math.sin(angleInRadians)),
    };
}

/**
 * y = k * x + b
 * 返回两点之间的斜率和b，x1 === x2 时返回null
 * @param point1
 * @param point2
 */
export function gradientAndBOfTwoPoints(point1, point2) {
    if (point2.x === point1.x) return null;
    const k = (point2.y - point1.y) / (point2.x - point1.x);
    const b = point1.y - (k * point1.x);
    return {
        k,
        b,
    };
}

/**
 * a/b = x/c  => x = a/b*c
 * 利用三角形相似已知三条边求第四条边
 * @param edgeA
 * @param edgeB
 * @param edgeC
 */
export function getLenghBySimilarTriangle(edgeA, edgeB, edgeC) {
    return edgeA / edgeB * edgeC;
}

/**
 * 返回旋转之后新的矩形四个点的坐标
 * @param originalFrame 原始图形位置大小
 * @param rotateAngle 顺时针旋转的角度
 * @param centerPoint 要绕转中心点
 * @returns {Array}
 */
export function getPointsAfterRotate(
    originalFrame, rotateAngle,
    centerPoint: IPoint | undefined = undefined,
): Array<IPoint> {
    const originPoints = [{
        x: originalFrame.left,
        y: originalFrame.top,
    }, {
        x: originalFrame.left + originalFrame.width,
        y: originalFrame.top,
    }, {
        x: originalFrame.left + originalFrame.width,
        y: originalFrame.top + originalFrame.height,
    }, {
        x: originalFrame.left,
        y: originalFrame.top + originalFrame.height,
    },
    ];
    let center = centerPoint;
    if (!center) {
        center = {
            x: (originalFrame.left + originalFrame.left + originalFrame.width) / 2,
            y: (originalFrame.top + originalFrame.top + originalFrame.height) / 2,
        };
    }
    const points: Array<IPoint> = [];
    for (let i = 0; i < originPoints.length; i++) {
        points.push(afterRotate(originPoints[i], rotateAngle, center.x, center.y));
    }
    return points;
}

/**
 * 返回旋转之后能够包裹的新的矩形位置大小
 * @param originalFrame 原始图形位置大小
 * @param rotateAngle 顺时针旋转的角度
 * @param centerPoint 要绕着旋转的点
 */
export function getFrameAfterRotate(
    originalFrame, rotateAngle = 0,
    centerPoint: IPoint | undefined = undefined,
) {
    const newPoints = getPointsAfterRotate(originalFrame, rotateAngle, centerPoint);
    const minX = Math.min(newPoints[0].x, newPoints[1].x, newPoints[2].x, newPoints[3].x);
    const minY = Math.min(newPoints[0].y, newPoints[1].y, newPoints[2].y, newPoints[3].y);
    const maxX = Math.max(newPoints[0].x, newPoints[1].x, newPoints[2].x, newPoints[3].x);
    const maxY = Math.max(newPoints[0].y, newPoints[1].y, newPoints[2].y, newPoints[3].y);
    return {
        left: minX,
        top: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

/**
 * 返回水平翻转之后新的矩形四个点的坐标
 * @param originalFrame 原始图形位置大小
 * @param center 翻转中心点
 * @returns {Array}
 */
export function getPointsAfterFlipH(originalFrame, center): Array<IPoint> {
    const originPoints = [{
        x: originalFrame.left,
        y: originalFrame.top,
    }, {
        x: originalFrame.left + originalFrame.width,
        y: originalFrame.top,
    }, {
        x: originalFrame.left + originalFrame.width,
        y: originalFrame.top + originalFrame.height,
    }, {
        x: originalFrame.left,
        y: originalFrame.top + originalFrame.height,
    },
    ];
    const points: Array<IPoint> = [];
    for (let i = 0; i < originPoints.length; i++) {
        points.push(afterFlipH(originPoints[i], center.x));
    }
    return points;
}

/**
 * 返回水平翻转之后的新的矩形位置大小
 * @param originalFrame 原始图形位置大小
 * @returns {Array}
 */
export function getFrameAfterFlipH(originalFrame, center) {
    const newPoints = getPointsAfterFlipH(originalFrame, center);
    const minX = Math.min(newPoints[0].x, newPoints[1].x, newPoints[2].x, newPoints[3].x);
    const minY = Math.min(newPoints[0].y, newPoints[1].y, newPoints[2].y, newPoints[3].y);
    const maxX = Math.max(newPoints[0].x, newPoints[1].x, newPoints[2].x, newPoints[3].x);
    const maxY = Math.max(newPoints[0].y, newPoints[1].y, newPoints[2].y, newPoints[3].y);
    return {
        left: minX,
        top: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

/**
 * 返回垂直翻转之后新的矩形四个点的坐标
 * @param originalFrame 原始图形位置大小
 * @param rotateAngle 顺时针旋转的角度
 * @returns {Array}
 */
export function getPointsAfterFlipV(originalFrame, center) {
    const originPoints = [{
        x: originalFrame.left,
        y: originalFrame.top,
    }, {
        x: originalFrame.left + originalFrame.width,
        y: originalFrame.top,
    }, {
        x: originalFrame.left + originalFrame.width,
        y: originalFrame.top + originalFrame.height,
    }, {
        x: originalFrame.left,
        y: originalFrame.top + originalFrame.height,
    },
    ];
    const points: Array<IPoint> = [];
    for (let i = 0; i < originPoints.length; i++) {
        points.push(afterFlipV(originPoints[i], center.y));
    }
    return points;
}


/**
 * 返回垂直翻转之后的新的矩形位置大小
 * @param originalFrame 原始图形位置大小
 * @returns {Array}
 */
export function getFrameAfterFlipV(originalFrame, center) {
    const newPoints = getPointsAfterFlipV(originalFrame, center);
    const minX = Math.min(newPoints[0].x, newPoints[1].x, newPoints[2].x, newPoints[3].x);
    const minY = Math.min(newPoints[0].y, newPoints[1].y, newPoints[2].y, newPoints[3].y);
    const maxX = Math.max(newPoints[0].x, newPoints[1].x, newPoints[2].x, newPoints[3].x);
    const maxY = Math.max(newPoints[0].y, newPoints[1].y, newPoints[2].y, newPoints[3].y);
    return {
        left: minX,
        top: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

export function centerPointOfRect(rect): IPoint | undefined {
    if (typeof rect?.right !== 'undefined' && typeof rect?.bottom !== 'undefined') {
        return {
            x: (rect.left + rect.right) / 2,
            y: (rect.top + rect.width) / 2,
        };
    }
    if (typeof rect?.width !== 'undefined' && typeof rect?.height !== 'undefined') {
        return {
            x: ((2 * rect.left) + rect.width) / 2,
            y: ((2 * rect.top) + rect.height) / 2,
        };
    }
}

export function cornerPointsOfRect(rect) {
    if (typeof rect?.right !== 'undefined' && typeof rect?.bottom !== 'undefined') {
        return [
            {
                x: rect.left,
                y: rect.top,
            },
            {
                x: rect.left,
                y: rect.bottom,
            },
            {
                x: rect.right,
                y: rect.top,
            },
            {
                x: rect.right,
                y: rect.bottom,
            },
        ];
    }
    if (typeof rect?.width !== 'undefined' && typeof rect?.height !== 'undefined') {
        return [
            {
                x: rect.left,
                y: rect.top,
            },
            {
                x: rect.left,
                y: rect.top + rect.height,
            },
            {
                x: rect.left + rect.width,
                y: rect.top,
            },
            {
                x: rect.left + rect.width,
                y: rect.top + rect.height,
            },
        ];
    }
}

// eslint-disable-next-line complexity
export function pointInRect(point, rect) {
    if (typeof rect?.right !== 'undefined' && typeof rect?.bottom !== 'undefined') {
        return point.x >= rect.left
        && point.x <= rect.right
        && point.y >= rect.top
        && point.y <= rect.bottom;
    }
    if (typeof rect?.width !== 'undefined' && typeof rect?.height !== 'undefined') {
        return point.x >= rect.left
            && point.x <= rect.left + rect.width
            && point.y >= rect.top
            && point.y <= rect.top + rect.height;
    }
}

/**
 * 椭圆周长
 * @param a
 * @param b
 */
export function perimeterOfEllipse(a, b) {
    const minR = Math.min(a, b);
    const maxR = Math.max(a, b);
    return (2 * Math.PI * minR) + (4 * (maxR - minR));
}

export function distanceBetweenTwoPoint(point1, point2) {
    return Math.sqrt(((point1.x - point2.x) * (point1.x - point2.x)) + ((point1.y - point2.y) * (point1.y - point2.y)));
}

export const verifyflipHValue = (flipH, flipV) => (flipH === 1 && flipV === 0) || (flipH === 0 && flipV === 1);

// /如果大于45于135 根据flip再特殊处理 触发长和宽对调
// eslint-disable-next-line complexity
export function checkChildrenNeedChangeWidthHeight(rotate, flipH, flipV) {
    if (rotate === 45 || rotate === 225) {
        // (flipH === 1 && flipV === 0) || (flipH === 0 && flipV === 1)
        if (verifyflipHValue(flipH, flipV)) {
            return false;
        }
        return true;
    } if (rotate === 135 || rotate === 315) {
        if (verifyflipHValue(flipH, flipV)) {
            return true;
        }
        return false;
    } if ((rotate > 45 && rotate < 135) || (rotate > 225 && rotate < 315)) {
        return true;
    }
    return false;
}


export const PointToSegDirection = {
    RIGHT: 1,
    LEFT: -1,
    ONSEG: 0,
};

export function pointDirectionForSeg(p, p1, p2) {
    const ret = ((p2.x - p1.x) * (p.y - p1.y)) - ((p2.y - p1.y) * (p.x - p1.x));
    if (ret < 0) {
        return PointToSegDirection.RIGHT;
    } if (ret > 0) {
        return PointToSegDirection.LEFT;
    }
    return PointToSegDirection.ONSEG;
}

export function matrixFlipH() {
    return [-1, 0, 0, 1, 0, 0];
}

export function matrixFlipV() {
    return [1, 0, 0, -1, 0, 0];
}

export function matrixRotate(deg) {
    const radian = angleToRadian(deg);
    return [Math.cos(radian), Math.sin(radian), -Math.sin(radian), Math.cos(radian), 0, 0];
}

/**
 * css matrix 乘法
 * @param arrayA
 * @param arrayB
 */
export function multiplyMatrix(arrayA, arrayB) {
    return [
        (arrayA[0] * arrayB[0]) + (arrayA[2] * arrayB[1]),
        (arrayA[1] * arrayB[0]) + (arrayA[3] * arrayB[1]),
        (arrayA[0] * arrayB[2]) + (arrayA[2] * arrayB[3]),
        (arrayA[1] * arrayB[2]) + (arrayA[3] * arrayB[3]),
        (arrayA[0] * arrayB[4]) + (arrayA[2] * arrayB[5]) + arrayA[4],
        (arrayA[1] * arrayB[4]) + (arrayA[3] * arrayB[5]) + arrayA[5],
    ];
}

export function matrixForFrame(frame) {
    let transformArray = [1, 0, 0, 1, 0, 0];
    if (frame.flipH && frame.flipH === 1) {
        transformArray = multiplyMatrix(matrixFlipH(), transformArray);
    }
    if (frame.flipV && frame.flipV === 1) {
        transformArray = multiplyMatrix(matrixFlipV(), transformArray);
    }
    if (frame.rotate) {
        transformArray = multiplyMatrix(matrixRotate(frame.rotate), transformArray);
    }
    return transformArray;
}

export function getPointAfterFlipAndRotate(originPoint, frame) {
    let point = { ...originPoint };
    const center: IPoint = centerPointOfRect(frame) || { x: 0, y: 0 };
    if (frame.flipH === 1) {
        point = afterFlipH(point, center?.x);
    }
    if (frame.flipV === 1) {
        point = afterFlipV(point, center?.y);
    }
    if (frame.rotate && frame.rotate % 360 !== 0) {
        point = afterRotate(point, frame.rotate, center?.x, center?.y);
    }
    return point;
}
